/**
 * N.B.: the logic of this module is also documented in docs/sphinx/source/reference/data-points.rst.
 *       if you change the logic, please revise also that documentation if needed
 * @module common/data-point-pickers
 * @category Misc
 */
import { safeAssert } from '@upright/common/safe-assert';
import { compact, isNil, min, minBy } from 'lodash';
import { ListHash } from '../../utils';
import { toStartAndEndDate } from '../date-ranges';
import { DateRange } from '../date-ranges/types';
import { WithSource } from '../input/companies/data-points/types';
import { CompanyProductSet } from '../input/companies/products/types';
import { isPublicSource } from '../input/data-sources';
import { isManualProductSetSource, isMicroworkProductSetSource } from '../input/data-sources/data-sources-not-in-db';
import { DataSourcePublic } from '../input/data-sources/types';
import { ModelEntityWithScores } from '../output/scores/model-entity-data/types';
import { FinalImpactScoreType } from '../output/scores/types';
import { composeComparator, Preference, toComparator } from './comparator';
import {
  ValidDateInterval,
  WithOptionalStartAndEndDate,
  WithResultIdAndOptionalDates,
  WithSourceAndCreatedAt,
  WithSourceAndOptionalYear,
  WithStartAndEndDate,
  WithYearAndSource,
} from './types';

function ensureDateValueIsDate(value: string | Date) {
  if (typeof value === 'string') {
    return new Date(value);
  }
  return value;
}

/**
 * Get a value for comparing a data point
 * (smaller values are prefererred)
 */
function productSetComparisonValue<T extends WithSourceAndCreatedAt>(datum: T) {
  if (!datum.createdAt || !datum.source) {
    throw Error(`Product set must have source and createdAt`);
  }
  const date = ensureDateValueIsDate(datum.createdAt);
  // datepart is always in the range 0 - 1
  const datePart = 1 - date.getTime() / new Date().getTime();
  const sourcePart = getSourceValue(datum.source);
  return sourcePart + datePart;
}

// N.B.: also consider productSetQualityToNumber
function getSourceValue(source: string) {
  if (isManualProductSetSource(source)) {
    return 0;
  }
  if (isMicroworkProductSetSource(source)) {
    return 5;
  }
  return 10; // should correspond to lite sets
}

export function productSetComparator<T extends WithSourceAndCreatedAt>(a: T, b: T) {
  return productSetComparisonValue(a) - productSetComparisonValue(b);
}

export function latestProductSetComparator<T extends WithSourceAndCreatedAt>(a: T, b: T) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

function sortDataSourcesByPriority(a: DataSourcePublic, b: DataSourcePublic): number {
  const priorityA = a.priority !== undefined ? a.priority : Number.MAX_SAFE_INTEGER;
  const priorityB = b.priority !== undefined ? b.priority : Number.MAX_SAFE_INTEGER;
  return priorityA - priorityB;
}

/**
 * Determine preference order of two data points according to the following logic
 *
 * * Always prefer public data points to non-public data points
 * * If above is similar, prefer data points with a higher year (i.e., describe a later period)
 * * If above is similar, prefer manual data sources
 * * If above is similar, prefer public data sources according to the priority of the public source
 *   (moot for private data sources)
 * * If above is similar, prefer data points created at a later date
 *
 * @param publicDataSources public (highly preferred) data sources
 * @returns a comparator function to compare between `WithYearAndSource` objects.
 */
export function buildYearAndSourceComparator(maybePublicDataSources: DataSourcePublic[] | undefined) {
  const publicDataSources = maybePublicDataSources || [];

  const publicityComparator = buildPublicityComparator(publicDataSources);
  const preferenceComparator = buildSourcePreferenceComparator(publicDataSources);
  return composeComparator<WithSourceAndOptionalYear>([
    inputVerifier,
    publicityComparator,
    yearComparator,
    preferenceComparator,
    createdAtComparator,
  ]);
}

function inputVerifier(left: WithSourceAndOptionalYear | undefined, right: WithSourceAndOptionalYear | undefined) {
  for (const datum of [left, right]) {
    if (!datum || !datum.source || !datum.createdAt) {
      throw Error(`Datum must have non-zero year and non-empty source`);
    }
  }
  return Preference.EITHER;
}

function buildPublicityComparator(publicDataSources: DataSourcePublic[]) {
  return toComparator(
    (datum: Pick<WithYearAndSource, 'source'>) => Number(isPublicSource(datum.source, publicDataSources)),
    'max',
  );
}

function buildSourcePreferenceComparator(publicDataSources: DataSourcePublic[]) {
  const preferenceOrder = ['manual', ...publicDataSources.sort(sortDataSourcesByPriority).map(s => s.id)];
  return toComparator((datum: Pick<WithYearAndSource, 'source'>) => {
    const index = preferenceOrder.findIndex(s => datum.source.startsWith(s));
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
  }, 'min');
}

const yearComparator = toComparator((datum: Pick<WithSourceAndOptionalYear, 'year'>) => datum.year ?? -1, 'max');
const createdAtComparator = toComparator((datum: Pick<WithYearAndSource, 'createdAt'>) => {
  const date = ensureDateValueIsDate(datum.createdAt);
  return date.getTime();
}, 'max');

/**
 * given a list of data points, and private and public data sources, return data points that match
 * the data sources
 *
 * @param data a list of data points to choose from
 * @param sources a hash indicating which sources the query has access to (points from other sources
 * are discarded)
 * @param publicDataSources public data sources that everyone has access to
 * @returns data points that are either public or present in `publicDataSources`
 */
export function filterDataPoints<T extends WithSource>(
  data: T[],
  sources: ListHash,
  publicDataSources: DataSourcePublic[] | undefined,
) {
  return data.filter(d => isPublicSource(d.source, publicDataSources) || !!sources[d.source]);
}

/**
 * Given a set of data points, accessible private and public data sources, and optional reference
 * period, find a preferred data point based on the following algorithm:
 *
 * * given all data points, consider those that are either from public data sources or explicitly
 *   given in `publicDataSources` (see [[filterDataPoints]])
 * * of the remaining data points, pick a preferred data point as per [[pickPreferredDataPoint]]
 *
 * @param data a list of data points to choose from
 * @param sources a hash indicating which sources the query has access to (points from other sources
 * are discarded)
 * @param publicDataSources public data sources that everyone has access to
 * @param referenceDates a time period for which to match data points
 * @returns the preferred data point if any is accessible according to sources or publicDataSources,
 * otherwise undefined
 */
export function pickPreferredDataPointWithSources<T extends WithSourceAndOptionalYear>(
  data: T[],
  sources: ListHash,
  publicDataSources: DataSourcePublic[] | undefined,
  referenceDates?: WithOptionalStartAndEndDate,
) {
  const allowedDataPoints = filterDataPoints(data, sources, publicDataSources);
  return pickPreferredDataPoint(allowedDataPoints, publicDataSources, referenceDates);
}

/**
 * Given a set of data points, accessible public data sources, and optional reference
 * period, find a preferred data point based on the following algorithm:
 *
 * * given all data points, consider those that are from public data sources
 * * of the remaining data points, pick a preferred data point as per [[pickPreferredDataPoint]]
 *
 * @param data a list of data points to choose from
 * @param publicDataSources public data sources that everyone has access to
 * @param referenceDates a time period for which to match data points
 * @returns the preferred data point if any is accessible according to publicDataSources, otherwise
 * undefined
 */
export function pickPreferredPublicDataPoint<T extends WithSourceAndOptionalYear>(
  data: T[],
  publicDataSources: DataSourcePublic[] | undefined,
  referenceDates?: WithOptionalStartAndEndDate,
): T | undefined {
  const publicDataPoints = data.filter(d => isPublicSource(d.source, publicDataSources));
  return pickPreferredDataPoint(publicDataPoints, publicDataSources, referenceDates);
}

/**
 * Converts data points that only have a year attribute
 * to have startDate and endDate so that they can be used with the
 * pickMultipleClosestModelResults function.
 */
function pickClosestDataPoints<T extends WithSourceAndOptionalYear>(
  data: T[],
  referenceDates: WithStartAndEndDate,
): T[] {
  return pickMultipleClosestDataPoints(
    data.map(d => {
      const { startDate, endDate } = toStartAndEndDate({
        dateRange: DateRange.YEAR,
        year: d.year ?? 1000, // 1000 looks like the lowest year that is safe to pass to toStartAndEndDate
      });
      return {
        ...d,
        startDate,
        endDate,
      };
    }),
    referenceDates,
  );
}

/**
 * Given a set of data points, public data sources, and optional reference period, find a preferred
 * data point based on the following algorithm:
 *
 * * if `referenceDates` are given, find the closest one or more data points according to
 *   [[pickMultipleClosestDataPoints]], otherwise consider all given data points
 * * use [[yearAndSourceComparator]] to find the preferred data point from the ones yielded above
 *
 * @param data a list of data points to choose from
 * @param publicDataSources public data sources that everyone has access to
 * @param referenceDates a time period for which to match data points
 * @returns the preferred data point if any is accessible according to publicDataSources, otherwise
 * undefined
 */
export function pickPreferredDataPoint<T extends WithSourceAndOptionalYear>(
  data: T[] | undefined,
  publicDataSources: DataSourcePublic[] | undefined,
  referenceDates?: WithOptionalStartAndEndDate,
): T | undefined {
  const sorted = sortPreferredDataPoints(data, publicDataSources, referenceDates);
  return sorted && sorted[0];
}

export function sortPreferredDataPoints<T extends WithSourceAndOptionalYear>(
  data: T[] | undefined,
  publicDataSources: DataSourcePublic[] | undefined,
  referenceDates?: WithOptionalStartAndEndDate,
): T[] | undefined {
  if (!data || data.length === 0) {
    return;
  }
  const filtered = hasStartAndEndDate(referenceDates) ? pickClosestDataPoints(data, referenceDates) : data;
  const comparator = buildYearAndSourceComparator(publicDataSources);
  const sorted = [...filtered].sort(comparator);
  return sorted;
}

export function pickPreferredPublicProductSet<T extends WithSourceAndCreatedAt>(data: T[]): T | undefined {
  const publicProductSets = data.filter(d => isManualProductSetSource(d.source));
  return pickPreferredProductSet(publicProductSets);
}

export function pickPreferredProductSet<T extends WithSourceAndCreatedAt>(data: T[]): T | undefined {
  if (data.length === 0) {
    return;
  }
  const sorted = [...data].sort(productSetComparator);
  return sorted[0];
}

// Used to pick an input datum that matches to the default output result
export function pickDefaultWeightSet<T extends WithOptionalStartAndEndDate>(weightSets: T[]): T {
  const picked = pickDefaultModelResult(weightSets);
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (!picked) {
    throw Error('Default weight set not found');
  }
  return picked;
}

function toValidDateInterval<T extends WithOptionalStartAndEndDate>(datum: T): ValidDateInterval<T> {
  const { startDate, endDate } = datum;
  if (!startDate || !endDate) {
    throw Error('Could not parse start or end date');
  }
  return { ...datum, startDate: toDate(startDate), endDate: toDate(endDate) };
}

export function isDateOutsideStartAndEndDate<T extends WithOptionalStartAndEndDate>(datum: T, referenceDate: Date) {
  const { startDate, endDate } = toValidDateInterval(datum);
  return startDate.getTime() > referenceDate.getTime() || endDate.getTime() < referenceDate.getTime();
}

/**
 * Used to pick an input datum that matches to the default output result. Used to determine
 * which product set *will* generate the default model result, even though that result does
 * not exist yet.
 * Throws error on empty array.
 */
export function pickDefaultProductSet<T extends CompanyProductSet>(productSets: T[]): T {
  return pickDatumClosestToCurrentTime(productSets);
}

/**
 * Reduce a datum with a date range to a point value (start date)
 *
 * @param dataWithDates data point with start and end dates
 * @returns the start date of the data point
 */
export function pickReferenceDate<T extends WithOptionalStartAndEndDate>(dataWithDates: T) {
  return dataWithDates.startDate;
}

export function pickDefaultModelResult<T extends WithOptionalStartAndEndDate>(data: T[] | undefined) {
  if (!data || !data.length) {
    throw Error(`Unexpected empty model results array`);
  }

  return pickDatumClosestToCurrentTime(data);
}

export function pickModelResultByIdOrDefault<
  T extends WithResultIdAndOptionalDates,
  ResultIdType extends string | number = number,
>(data: T[], resultId: ResultIdType | undefined) {
  return data.find(d => d.resultId === resultId) ?? pickDefaultModelResult(data);
}

function toDate(stringOrDate: string | Date): Date {
  return typeof stringOrDate === 'string' ? new Date(stringOrDate) : stringOrDate;
}

function absDistanceBetweenPeriods(a: WithOptionalStartAndEndDate, b: WithOptionalStartAndEndDate) {
  const [refA, refB] = [pickReferenceDate(a), pickReferenceDate(b)];
  if (refA === undefined || refB === undefined) {
    throw new Error('Could not parse reference dates for one or more periods');
  }
  const [dateA, dateB] = [toDate(refA), toDate(refB)];
  return Math.abs(dateB.getTime() - dateA.getTime());
}

const dayInMs = 86400000;
function isClose(a: number, b: number, tolerance: number) {
  return Math.abs(a - b) <= tolerance;
}

export function pickClosestModelResult<T extends WithOptionalStartAndEndDate>(
  dataWithDates: T[] | undefined,
  referenceDates: WithOptionalStartAndEndDate,
) {
  if (!dataWithDates || !dataWithDates.length) {
    throw Error(`Unexpected empty model results array`);
  }
  const reference = pickReferenceDate(referenceDates);
  if (!reference) {
    return pickDefaultModelResult(dataWithDates);
  }
  const referenceDate = toDate(reference);
  return pickDatumClosestToGivenTime(referenceDate, dataWithDates);
}

/**
 * Find closest data points to a given reference date. Distance is determined by reducing the time
 * frames of each datum as well as `referenceDates` to point values using [[pickReferenceDate]].
 * Closest data points are defined as data points whose distance to the reference date is within
 * 3 days of the smallest distance (rationale: we typically talk about years, so 3 days is enough
 * to ignore noise from leap days and timezones)
 *
 * NOTE: this function is used to picking multiple data-points in cases where the data has other dimensions (such as context) that are filtered after time filtering
 *
 * @param dataWithDates a set of data points that have a start and end date associated with them
 * @param referenceDates a reference period to which to match the data points
 * @returns one or more data points that are closest to the reference period
 * @throws if `dataWithDates` is an empty array
 */
export function pickMultipleClosestDataPoints<T extends WithOptionalStartAndEndDate>(
  dataWithDates: T[] | undefined,
  referenceDates: WithStartAndEndDate,
): T[] {
  if (!dataWithDates || !dataWithDates.length) {
    throw Error('Cannot pick data points from an empty array');
  }
  try {
    // Help TS: we know dataWithDates has members => min will not return undefined
    const shortestDistance = min(dataWithDates.map(datum => absDistanceBetweenPeriods(datum, referenceDates)))!;
    return dataWithDates.filter(datum =>
      isClose(absDistanceBetweenPeriods(datum, referenceDates), shortestDistance, dayInMs * 3),
    );
  } catch (error) {
    console.error(error instanceof Error ? error.message : error);
    return compact([pickDefaultModelResult(dataWithDates)]);
  }
}

export function pickDatumClosestToCurrentTime<T extends WithOptionalStartAndEndDate>(dataWithDates: T[]) {
  return pickDatumClosestToGivenTime(new Date(), dataWithDates);
}

export function pickDatumClosestToGivenTime<T extends WithOptionalStartAndEndDate>(
  referenceDate: Date,
  dataWithDates: T[],
): T {
  if (!dataWithDates.length) {
    throw Error('Cannot pick closest datum from an empty array');
  }
  const datumWithOverlap = findOverlappingDatum(referenceDate, dataWithDates);
  if (datumWithOverlap) {
    return datumWithOverlap;
  }
  return pickDatumWithClosestIntervalEndpoint(referenceDate, dataWithDates);
}

function findOverlappingDatum<T extends WithOptionalStartAndEndDate>(
  referenceDate: Date,
  dataWithDates: T[],
): T | null {
  return dataWithDates.find(datum => isDateOverlappingInterval(referenceDate, datum)) ?? null;
}

function pickDatumWithClosestIntervalEndpoint<T extends WithOptionalStartAndEndDate>(
  referenceDate: Date,
  dataWithDates: T[],
): T {
  if (!dataWithDates.length) {
    throw Error('Cannot pick closest datum from an empty array');
  }
  const chosenDatum = minBy(dataWithDates, datum => {
    const distanceToStart = distance(referenceDate, datum.startDate) ?? Infinity;
    const distanceToEnd = distance(referenceDate, datum.endDate) ?? Infinity;
    return Math.min(distanceToStart, distanceToEnd);
  });
  safeAssert(chosenDatum);
  return chosenDatum;
}

function distance(dateA: Date, dateB: Date | string | undefined) {
  if (dateB === undefined) {
    return undefined;
  }
  return Math.abs(toDate(dateB).getTime() - dateA.getTime());
}

function isDateOverlappingInterval(
  date: Date,
  interval: { startDate: string | Date | undefined; endDate: string | Date | undefined },
) {
  if (interval.startDate === undefined || interval.endDate === undefined) {
    return false;
  }
  const referenceTime = date.getTime();
  const intervalStartTime = toDate(interval.startDate).getTime();
  if (referenceTime === intervalStartTime) {
    return true;
  }
  const intervalEndTime = toDate(interval.endDate).getTime();
  return intervalStartTime <= referenceTime && referenceTime < intervalEndTime;
}

export function getDefaultNetImpactRatioOfEntity(entity: ModelEntityWithScores) {
  const modelResult = pickDefaultModelResult(entity.modelResults);
  const netImpactRatio = modelResult.netScoresByType[FinalImpactScoreType.RELATIVE];
  return netImpactRatio;
}

type MaybeUnmatched<T> = T & { entityType?: string; entityId?: string };
export type Matched<T> = T & { entityType: string; entityId: string };

export function itemIsMatched<T>(item: MaybeUnmatched<T>): item is Matched<T> {
  if (item.entityType && item.entityId) {
    return true;
  }
  return false;
}

export function hasStartAndEndDate(datum: WithOptionalStartAndEndDate | undefined): datum is WithStartAndEndDate {
  return !!datum && !isNil(datum.startDate) && !isNil(datum.endDate);
}
